/**
 * AgentActivityTimeline — vertical timeline of recent AGENT_DECISION
 * + RULE_VALIDATION episodes for a playbook.
 *
 * Reads `recent_activity` from the playbook detail payload (server
 * pulls from the local episode log). Each item shows: timestamp,
 * source channel, agent_type icon, decision_type label, decision
 * text, and (when present) the rule_id / persona_id / brief_id
 * cross-reference badges.
 */

import { clsx } from 'clsx';
import {
  Bot,
  User,
  Sparkles,
  ShieldCheck,
  Archive,
  Activity as ActivityIcon,
} from 'lucide-react';
import { type CreativeMemoryActivityItem } from '@/services/api';

interface AgentActivityTimelineProps {
  items: CreativeMemoryActivityItem[];
}

const formatRelative = (iso?: string): string => {
  if (!iso) return '—';
  try {
    const ts = new Date(iso).getTime();
    const diffMs = Date.now() - ts;
    if (diffMs < 60_000) return `${Math.round(diffMs / 1000)}s ago`;
    if (diffMs < 3_600_000) return `${Math.round(diffMs / 60_000)}m ago`;
    if (diffMs < 86_400_000) return `${Math.round(diffMs / 3_600_000)}h ago`;
    return `${Math.round(diffMs / 86_400_000)}d ago`;
  } catch {
    return iso.slice(0, 10);
  }
};

const channelClass = (ch?: string): string => {
  switch ((ch || '').toLowerCase()) {
    case 'mcp':
      return 'bg-violet-100 text-violet-700';
    case 'claude':
      return 'bg-amber-100 text-amber-700';
    case 'inapp':
      return 'bg-sky-100 text-sky-700';
    case 'system':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const iconForItem = (item: CreativeMemoryActivityItem): React.ReactNode => {
  if (item.episode_type === 'rule_validation') {
    if (item.action === 'archive')
      return <Archive className="w-3 h-3 text-slate-500" />;
    return <ShieldCheck className="w-3 h-3 text-emerald-500" />;
  }
  switch ((item.agent_type || '').toLowerCase()) {
    case 'creative_review_agent':
      return <Sparkles className="w-3 h-3 text-amber-500" />;
    case 'creative_agent':
      return <Bot className="w-3 h-3 text-brand-teal" />;
    case 'human':
      return <User className="w-3 h-3 text-violet-500" />;
    default:
      return <ActivityIcon className="w-3 h-3 text-slate-500" />;
  }
};

export function AgentActivityTimeline({ items }: AgentActivityTimelineProps) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-xs text-slate-500">
        No agent activity captured yet. Decisions made via MCP tools or
        admin actions will appear here.
      </div>
    );
  }

  return (
    <ol className="relative space-y-3 border-l border-slate-200 pl-5">
      {items.map((item, idx) => (
        <li key={item.episode_id || idx} className="relative">
          <span className="absolute -left-[26px] top-1 flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white">
            {iconForItem(item)}
          </span>
          <div className="rounded border border-slate-200 bg-white px-3 py-2.5">
            <header className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[11px] font-semibold text-slate-700">
                {item.decision_type ||
                  item.action ||
                  item.episode_type ||
                  'event'}
              </span>
              <span
                className={clsx(
                  'rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide font-medium',
                  channelClass(item.source_channel),
                )}
              >
                {item.source_channel || 'system'}
              </span>
              <span className="ml-auto text-[10px] text-slate-400">
                {formatRelative(item.ts)}
              </span>
            </header>
            {(item.decision || item.rationale) && (
              <p className="mt-1 text-[12px] text-slate-700 leading-snug">
                {item.decision || item.rationale}
              </p>
            )}
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] text-slate-500">
              {item.rule_id && (
                <span className="rounded bg-slate-100 px-1 py-0.5 font-mono">
                  rule {item.rule_id}
                </span>
              )}
              {item.persona_id && (
                <span className="rounded bg-violet-50 px-1 py-0.5">
                  persona {item.persona_id}
                </span>
              )}
              {item.creative_id && (
                <span className="rounded bg-sky-50 px-1 py-0.5">
                  creative {item.creative_id}
                </span>
              )}
              {item.actor && (
                <span className="rounded bg-emerald-50 px-1 py-0.5">
                  by {item.actor}
                </span>
              )}
              {item.rules_consulted?.length > 0 && (
                <span className="rounded bg-slate-100 px-1 py-0.5">
                  {item.rules_consulted.length} rules consulted
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
